import usePageTitle from '../../hooks/usePageTitle';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { admin, orders as ordersApi } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Package, 
  ShoppingCart, 
  Users, 
  DollarSign,
  TrendingUp,
  Eye
} from 'lucide-react';

const AdminDashboard = () => {
  usePageTitle('Admin Dashboard');
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, ordersRes] = await Promise.all([
        admin.getStats(),
        ordersApi.getAll({ limit: 5 })
      ]);
      setStats(statsRes.data);
      setRecentOrders(ordersRes.data.orders);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      processing: 'bg-purple-100 text-purple-800',
      shipped: 'bg-indigo-100 text-indigo-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-white w-1/4" />
        <div className="grid grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-white" />
          ))}
        </div>
      </div>
    );
  }

  const statCards = [
    {
      name: 'Total Revenue',
      value: `$${stats?.total_revenue?.toFixed(2) || '0.00'}`,
      icon: DollarSign,
      color: 'text-green-600',
      bg: 'bg-green-100'
    },
    {
      name: 'Total Orders',
      value: stats?.total_orders || 0,
      icon: ShoppingCart,
      color: 'text-blue-600',
      bg: 'bg-blue-100'
    },
    {
      name: 'Products',
      value: stats?.total_products || 0,
      icon: Package,
      color: 'text-purple-600',
      bg: 'bg-purple-100'
    },
    {
      name: 'Customers',
      value: stats?.total_users || 0,
      icon: Users,
      color: 'text-orange-600',
      bg: 'bg-orange-100'
    }
  ];

  return (
    <div className="space-y-8 fade-in" data-testid="admin-dashboard">
      <div>
        <h1 className="text-2xl font-bold text-[#0F172A]">Dashboard</h1>
        <p className="text-[#64748B]">Welcome to the admin panel</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => (
          <div key={stat.name} className="bg-white border border-[#E2E8F0] p-6">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 ${stat.bg} flex items-center justify-center`}>
                <stat.icon className={`w-6 h-6 ${stat.color}`} />
              </div>
              <div>
                <p className="text-sm text-[#64748B]">{stat.name}</p>
                <p className="text-2xl font-bold text-[#0F172A]">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pending Orders Alert */}
      {stats?.pending_orders > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <TrendingUp className="w-5 h-5 text-yellow-600" />
            <span className="text-yellow-800">
              You have <strong>{stats.pending_orders}</strong> pending order(s) to review
            </span>
          </div>
          <Link to="/admin/orders">
            <Button variant="outline" size="sm" className="border-yellow-400 text-yellow-700 hover:bg-yellow-100">
              View Orders
            </Button>
          </Link>
        </div>
      )}

      {/* Recent Orders */}
      <div className="bg-white border border-[#E2E8F0]">
        <div className="p-6 border-b border-[#E2E8F0] flex justify-between items-center">
          <h2 className="text-lg font-semibold text-[#0F172A]">Recent Orders</h2>
          <Link to="/admin/orders">
            <Button variant="ghost" size="sm">View All</Button>
          </Link>
        </div>

        {recentOrders.length === 0 ? (
          <div className="p-12 text-center text-[#64748B]">
            No orders yet
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F8FAFC]">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-[#64748B]">Order</th>
                  <th className="text-left p-4 text-sm font-medium text-[#64748B]">Customer</th>
                  <th className="text-left p-4 text-sm font-medium text-[#64748B]">Status</th>
                  <th className="text-left p-4 text-sm font-medium text-[#64748B]">Total</th>
                  <th className="text-left p-4 text-sm font-medium text-[#64748B]">Date</th>
                  <th className="text-left p-4"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]">
                {recentOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-[#F8FAFC]">
                    <td className="p-4 font-mono text-sm">{order.order_number}</td>
                    <td className="p-4 text-sm">{order.company_name}</td>
                    <td className="p-4">
                      <Badge className={getStatusColor(order.status)}>
                        {order.status}
                      </Badge>
                    </td>
                    <td className="p-4 font-semibold">${order.total.toFixed(2)}</td>
                    <td className="p-4 text-sm text-[#64748B]">
                      {new Date(order.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-4">
                      <Link to={`/orders/${order.id}`}>
                        <Button variant="ghost" size="sm">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
